import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { adminListQualityAlerts, adminResolveQualityAlert } from "@/lib/quality-alerts.functions";
import { Loader2, AlertTriangle, CheckCircle2, Gauge, Image as ImageIcon, Activity } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/quality")({
  head: () => ({ meta: [{ title: "Quality alerts — Admin — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: AdminQuality,
});

type Alert = {
  id: string; source: "rum" | "lighthouse" | "visual"; severity: "info" | "warning" | "critical";
  route: string; metric: string | null; value: number | null; threshold: number | null;
  window_start: string | null; window_end: string | null; payload: Record<string, unknown>;
  resolved: boolean; resolved_at: string | null; created_at: string;
};

const SOURCE_ICON = { rum: Activity, lighthouse: Gauge, visual: ImageIcon } as const;

function AdminQuality() {
  const navigate = useNavigate();
  const fetchAlerts = useServerFn(adminListQualityAlerts);
  const resolveAlert = useServerFn(adminResolveQualityAlert);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [filter, setFilter] = useState<"all" | "rum" | "lighthouse" | "visual">("all");
  const [includeResolved, setIncludeResolved] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetchAlerts({ data: { source: filter === "all" ? undefined : filter, includeResolved } });
      setAlerts(res.alerts as Alert[]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load");
    }
  }, [fetchAlerts, filter, includeResolved]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) { navigate({ to: "/auth", search: { redirect: "/admin/quality" } }); return; }
      await load();
      setLoading(false);
    })();
  }, [navigate, load]);

  useEffect(() => {
    const ch = supabase
      .channel("admin-quality")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "quality_alerts" }, () => void load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [load]);

  async function onResolve(id: string) {
    try { await resolveAlert({ data: { id } }); toast.success("Resolved"); await load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Failed"); }
  }

  if (loading) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-7xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">Quality alerts</h1>
            <p className="mt-1 text-sm text-muted-foreground">RUM Web Vitals, Lighthouse CI, and Playwright visual regressions.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(["all", "rum", "lighthouse", "visual"] as const).map((s) => (
              <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)}>{s}</Button>
            ))}
            <Button size="sm" variant={includeResolved ? "default" : "outline"} onClick={() => setIncludeResolved((v) => !v)}>
              {includeResolved ? "Hide resolved" : "Show resolved"}
            </Button>
            <Button asChild size="sm" variant="outline"><Link to="/admin/ops">Ops</Link></Button>
          </div>
        </div>

        {alerts.length === 0 ? (
          <p className="mt-10 rounded-2xl border border-dashed border-border bg-card p-12 text-center text-sm text-muted-foreground">No alerts. 🎉</p>
        ) : (
          <ul className="mt-6 space-y-3">
            {alerts.map((a) => {
              const Icon = SOURCE_ICON[a.source] ?? AlertTriangle;
              const sevCls = a.severity === "critical" ? "border-red-200 bg-red-50" : a.severity === "warning" ? "border-amber-200 bg-amber-50" : "border-border bg-card";
              return (
                <li key={a.id} className={`rounded-2xl border p-4 ${sevCls}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                        <span className="grid h-7 w-7 place-items-center rounded-lg bg-background"><Icon className="h-4 w-4" /></span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs uppercase">{a.source}</span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{a.severity}</span>
                        {a.metric && <span className="font-mono text-xs">{a.metric}</span>}
                        {a.resolved && <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" />resolved</span>}
                      </p>
                      <p className="mt-1.5 break-all font-mono text-xs text-muted-foreground">
                        <a href={a.route} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline">{a.route}</a>
                      </p>
                      {(a.value != null || a.threshold != null) && (
                        <p className="mt-1 text-sm">
                          {a.value != null && <span>value <strong className="tabular-nums">{a.value}</strong></span>}
                          {a.threshold != null && <span className="ml-3 text-muted-foreground">threshold {a.threshold}</span>}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {new Date(a.created_at).toLocaleString("en-GB")}
                        {a.window_start && a.window_end && (
                          <span> · window {new Date(a.window_start).toLocaleTimeString("en-GB")} → {new Date(a.window_end).toLocaleTimeString("en-GB")}</span>
                        )}
                      </p>
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-muted-foreground">Payload</summary>
                        <pre className="mt-1 max-h-64 overflow-auto rounded-md bg-background p-2 text-[11px]">{JSON.stringify(a.payload, null, 2)}</pre>
                      </details>
                    </div>
                    {!a.resolved && <Button size="sm" variant="outline" onClick={() => onResolve(a.id)}>Mark resolved</Button>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
}
